import { useForm } from "@inertiajs/react";
import { Box, Button, Grid, Select, Text, TextArea, TextField } from "@radix-ui/themes";
import type { FormEvent } from "react";

type UserOption = { id: number; email: string };
type GroupOption = { id: number; name: string };
type ProjectFormValues = {
  name: string;
  status: string;
  project_group_id: string;
  project_owner_id: string;
  project_manager_id: string;
  frontend_lead_id: string;
  backend_lead_id: string;
  harvest_project_id: string;
  quickbooks_customer_id: string;
  asana_workspace_id: string;
  google_drive_folder_id: string;
  vcs_repository_links: string;
};

export function ProjectForm({
  action,
  method,
  users,
  groups,
  initialValues,
}: {
  action: string;
  method: "post" | "put" | "patch";
  users: UserOption[];
  groups: GroupOption[];
  initialValues: ProjectFormValues;
}) {
  const { data, setData, post, processing, errors, transform } = useForm(initialValues);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = {
      ...data,
      vcs_repository_links: data.vcs_repository_links
        .split("\n")
        .map((value) => value.trim())
        .filter(Boolean),
    };

    transform(() => ({
      ...payload,
      ...(method === "post" ? {} : { _method: method.toUpperCase() }),
    }));

    if (method === "post") {
      post(action);
      return;
    }

    post(action);
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <Box>
        <Text as="label" size="2" weight="medium">
          Name
        </Text>
        <TextField.Root
          mt="1"
          value={data.name}
          onChange={(event) => setData("name", event.target.value)}
        />
        {errors.name && (
          <Text size="1" color="red">
            {errors.name}
          </Text>
        )}
      </Box>
      <Box>
        <Text as="label" size="2" weight="medium">
          Status
        </Text>
        <Select.Root value={data.status} onValueChange={(value) => setData("status", value)}>
          <Select.Trigger mt="1" />
          <Select.Content>
            <Select.Item value="lead">Lead</Select.Item>
            <Select.Item value="active">Active</Select.Item>
            <Select.Item value="paused">Paused</Select.Item>
            <Select.Item value="archived">Archived</Select.Item>
          </Select.Content>
        </Select.Root>
      </Box>
      <Box>
        <Text as="label" size="2" weight="medium">
          Project Group
        </Text>
        <Select.Root
          value={data.project_group_id || "__none__"}
          onValueChange={(value) => setData("project_group_id", value === "__none__" ? "" : value)}
        >
          <Select.Trigger mt="1" placeholder="Select group" />
          <Select.Content>
            <Select.Item value="__none__">None</Select.Item>
            {groups.map((group) => (
              <Select.Item key={group.id} value={String(group.id)}>
                {group.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Box>

      {[
        ["project_owner_id", "Project Owner (PO)"],
        ["project_manager_id", "Project Manager (PM)"],
        ["frontend_lead_id", "Frontend Lead (FE)"],
        ["backend_lead_id", "Backend Lead (BE)"],
      ].map(([key, label]) => (
        <Box key={key}>
          <Text as="label" size="2" weight="medium">
            {label}
          </Text>
          <Select.Root
            value={(data[key as keyof ProjectFormValues] as string) || undefined}
            onValueChange={(value) => setData(key as keyof ProjectFormValues, value)}
          >
            <Select.Trigger mt="1" placeholder="Select user" />
            <Select.Content>
              {users.map((user) => (
                <Select.Item key={user.id} value={String(user.id)}>
                  {user.email}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {errors[key as keyof typeof errors] && (
            <Text size="1" color="red">
              {errors[key as keyof typeof errors]}
            </Text>
          )}
        </Box>
      ))}

      <Grid columns={{ initial: "1", md: "2" }} gap="3">
        <Box>
          <Text as="label" size="2" weight="medium">
            Harvest Project ID
          </Text>
          <TextField.Root
            mt="1"
            value={data.harvest_project_id}
            onChange={(event) => setData("harvest_project_id", event.target.value)}
          />
        </Box>
        <Box>
          <Text as="label" size="2" weight="medium">
            QuickBooks Customer ID
          </Text>
          <TextField.Root
            mt="1"
            value={data.quickbooks_customer_id}
            onChange={(event) => setData("quickbooks_customer_id", event.target.value)}
          />
        </Box>
        <Box>
          <Text as="label" size="2" weight="medium">
            Asana Workspace ID
          </Text>
          <TextField.Root
            mt="1"
            value={data.asana_workspace_id}
            onChange={(event) => setData("asana_workspace_id", event.target.value)}
          />
        </Box>
        <Box>
          <Text as="label" size="2" weight="medium">
            Google Drive Folder ID
          </Text>
          <TextField.Root
            mt="1"
            value={data.google_drive_folder_id}
            onChange={(event) => setData("google_drive_folder_id", event.target.value)}
          />
        </Box>
      </Grid>

      <Box>
        <Text as="label" size="2" weight="medium">
          VCS repository links (one per line)
        </Text>
        <TextArea
          mt="1"
          size="2"
          value={data.vcs_repository_links}
          onChange={(event) => setData("vcs_repository_links", event.target.value)}
        />
      </Box>

      <Button type="submit" disabled={processing}>
        Save Project
      </Button>
    </form>
  );
}
